import { Building2, Compass, Target, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { GtmBrain } from "../api/GtmApi";
import type { MarketProfileDocId } from "./marketProfile";

type FieldKind = "text" | "textarea" | "list";

type DocField = {
  id: keyof GtmBrain & string;
  label: string;
  kind: FieldKind;
  placeholder?: string;
  rows?: number;
};

const DOC_META: Record<
  MarketProfileDocId,
  { title: string; subtitle: string; icon: "overview" | "icp" | "company" }
> = {
  overview: {
    title: "Overview",
    subtitle: "What you do, why it matters, and the evidence behind it.",
    icon: "overview",
  },
  icp: {
    title: "Ideal customer profile",
    subtitle: "Who buys, who champions, and who uses the product.",
    icon: "icp",
  },
  company: {
    title: "Company",
    subtitle: "Identity, size, stage, and how you show up in market.",
    icon: "company",
  },
};

const OVERVIEW_FIELDS: DocField[] = [
  {
    id: "valueProposition",
    label: "Value proposition",
    kind: "textarea",
    rows: 3,
    placeholder: "The outcome you sell, in one breath.",
  },
  {
    id: "businessSummary",
    label: "Business summary",
    kind: "textarea",
    rows: 4,
    placeholder: "What the business does and for whom.",
  },
  {
    id: "offering",
    label: "Offering",
    kind: "textarea",
    rows: 3,
    placeholder: "Product or service in plain language.",
  },
  {
    id: "keyFeatures",
    label: "Key features",
    kind: "textarea",
    rows: 3,
    placeholder: "The capabilities that matter most.",
  },
  {
    id: "socialProof",
    label: "Social proof",
    kind: "textarea",
    rows: 3,
    placeholder: "Customers, logos, metrics, or quotes.",
  },
  {
    id: "industries",
    label: "Industries",
    kind: "list",
    rows: 3,
    placeholder: "One per line\nSaaS\nFinTech",
  },
  {
    id: "geographies",
    label: "Geographies",
    kind: "list",
    rows: 3,
    placeholder: "One per line\nNorth America\nEMEA",
  },
];

const ICP_FIELDS: DocField[] = [
  {
    id: "icpSummary",
    label: "ICP summary",
    kind: "textarea",
    rows: 4,
    placeholder: "Who is the ideal customer, in a few sentences.",
  },
  {
    id: "targetCompanySize",
    label: "Target company size",
    kind: "text",
    placeholder: "e.g. 50–500 employees, Series B+",
  },
  {
    id: "verticals",
    label: "Verticals",
    kind: "list",
    rows: 3,
    placeholder: "One per line\nB2B SaaS\nMarketplaces",
  },
  {
    id: "buyingRoles",
    label: "Buying roles",
    kind: "list",
    rows: 3,
    placeholder: "One per line\nVP Engineering\nHead of QA",
  },
  {
    id: "championRoles",
    label: "Champion roles",
    kind: "list",
    rows: 3,
    placeholder: "One per line\nQA Lead\nDevOps Manager",
  },
  {
    id: "userRoles",
    label: "User roles",
    kind: "list",
    rows: 3,
    placeholder: "One per line\nQA Engineer\nSDET",
  },
];

const COMPANY_FIELDS: DocField[] = [
  { id: "companyName", label: "Company name", kind: "text", placeholder: "Acme Inc." },
  { id: "domain", label: "Domain", kind: "text", placeholder: "acme.com" },
  {
    id: "linkedinUrl",
    label: "LinkedIn",
    kind: "text",
    placeholder: "linkedin.com/company/…",
  },
  {
    id: "companySize",
    label: "Company size",
    kind: "text",
    placeholder: "e.g. 120 people",
  },
  {
    id: "businessModel",
    label: "Business model",
    kind: "text",
    placeholder: "e.g. B2B SaaS subscription",
  },
  { id: "stage", label: "Stage", kind: "text", placeholder: "e.g. Series A" },
  { id: "language", label: "Language", kind: "text", placeholder: "e.g. English" },
];

function fieldsFor(docId: MarketProfileDocId): DocField[] {
  if (docId === "icp") return ICP_FIELDS;
  if (docId === "company") return COMPANY_FIELDS;
  return OVERVIEW_FIELDS;
}

function listText(value?: string[] | null): string {
  return (value || []).filter(Boolean).join("\n");
}

function parseList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((v) => v.trim())
    .filter(Boolean);
}

function fieldValue(brain: GtmBrain, field: DocField): string {
  if (field.kind === "list") {
    return listText(brain[field.id] as string[] | undefined);
  }
  return ((brain[field.id] as string | undefined) || "");
}

type Props = {
  brain: GtmBrain;
  docId: MarketProfileDocId;
  onBrainChange: (next: GtmBrain) => void;
  onBackToMarket: () => void;
  onBackToOverview: () => void;
};

export default function BrainMarketDocView({
  brain,
  docId,
  onBrainChange,
  onBackToMarket,
  onBackToOverview,
}: Props) {
  const meta = DOC_META[docId];
  const fields = fieldsFor(docId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [listDrafts, setListDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setListDrafts({});
  }, [docId]);

  const scrollToSection = (id: string) => {
    const el = scrollRef.current?.querySelector(`[data-section-id="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const updateField = (field: DocField, raw: string) => {
    if (field.kind === "list") {
      setListDrafts((prev) => ({ ...prev, [field.id]: raw }));
      onBrainChange({ ...brain, [field.id]: parseList(raw) });
      return;
    }
    onBrainChange({ ...brain, [field.id]: raw });
  };

  const displayValue = (field: DocField): string => {
    if (field.kind === "list" && listDrafts[field.id] !== undefined) {
      return listDrafts[field.id];
    }
    return fieldValue(brain, field);
  };

  const Icon =
    meta.icon === "icp" ? Target : meta.icon === "company" ? Building2 : Compass;

  return (
    <div className="flex h-full w-[min(100%,28rem)] shrink-0 min-w-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
        <div className="min-w-0 truncate">
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToOverview}
          >
            Brain
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <button
            type="button"
            className="hover:text-slate-800 hover:underline"
            onClick={onBackToMarket}
          >
            Market profile
          </button>
          <span className="mx-1.5 text-slate-300">›</span>
          <span className="font-medium text-slate-800">{meta.title}</span>
        </div>
        <button
          type="button"
          onClick={onBackToMarket}
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50">
          <Icon className="h-4 w-4 text-rose-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900">{meta.title}</h2>
        <p className="mt-1 text-sm text-slate-500">{meta.subtitle}</p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {fields.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => scrollToSection(f.id)}
              className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-slate-300 hover:bg-white hover:text-slate-900"
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          {fields.map((f) => (
            <section key={f.id} data-section-id={f.id} className="scroll-mt-4">
              <label className="block text-sm font-semibold text-slate-900">{f.label}</label>
              {f.kind === "text" ? (
                <input
                  type="text"
                  value={displayValue(f)}
                  onChange={(e) => updateField(f, e.target.value)}
                  placeholder={f.placeholder}
                  className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
                />
              ) : (
                <textarea
                  value={displayValue(f)}
                  onChange={(e) => updateField(f, e.target.value)}
                  placeholder={f.placeholder}
                  rows={f.rows || 3}
                  className="mt-2 w-full resize-y rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300"
                />
              )}
              {f.kind === "list" ? (
                <p className="mt-1 text-[11px] text-slate-400">One per line (commas also work)</p>
              ) : null}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
